import type { Student, SeatingChart, SeatingAssignment } from "../../types/models";
import { buildDistanceMatrix } from "./distance";
import { checkAssignmentConstraints, getApartPairs } from "./constraints";
import { shuffle } from "@logic/random";

/**
 * Score a single student placement
 * Higher score = better placement
 */
function scoreStudentPlacement(
    studentId: string,
    seatId: string,
    chart: SeatingChart,
    assignments: Map<string, string | undefined>,
    apartPairs: [string, string, number][]
): number {
    const matrix = buildDistanceMatrix(chart);
    let score = 0;

    // Penalize "apart" constraint violations
    for (const [idA, idB, minDistance] of apartPairs) {
        let otherStudentId: string | null = null;

        if (idA === studentId) {
            otherStudentId = idB;
        } else if (idB === studentId) {
            otherStudentId = idA;
        }

        if (otherStudentId) {
            for (const [otherSeatId, assignedId] of assignments) {
                if (assignedId === otherStudentId) {
                    const distance = matrix.get(seatId, otherSeatId);
                    if (distance < minDistance && distance !== Infinity) {
                        // Penalty proportional to how much constraint is violated
                        score -= (minDistance - distance) * 10;
                    } else {
                        // Bonus for satisfying constraint
                        score += 5;
                    }
                }
            }
        }
    }

    return score;
}

/**
 * Calculate total score for an assignment
 */
export function scoreAssignment(
    assignment: SeatingAssignment,
    chart: SeatingChart,
    students: Student[]
): number {
    const violations = checkAssignmentConstraints(assignment, chart, students);

    // Base score: penalize each violation heavily
    let score = -violations.length * 100;

    // Add bonus for each satisfied constraint
    const totalConstraints = chart.proximityConstraints.length;
    const satisfiedRatio = 1 - (violations.length / Math.max(totalConstraints, 1));
    score += satisfiedRatio * 50;

    return score;
}

/**
 * Try swapping two students and see if it improves the score
 */
function trySwap(
    seatIdA: string,
    seatIdB: string,
    assignments: Map<string, string | undefined>,
    chart: SeatingChart,
    students: Student[]
): { improved: boolean; scoreDelta: number } {
    const studentA = assignments.get(seatIdA);
    const studentB = assignments.get(seatIdB);

    // Skip if both empty or same student
    if (!studentA && !studentB) return { improved: false, scoreDelta: 0 };
    if (studentA === studentB) return { improved: false, scoreDelta: 0 };

    // Calculate current score
    const currentAssignment: SeatingAssignment = {
        seatAssignments: assignments,
        timestamp: Date.now()
    };
    const currentScore = scoreAssignment(currentAssignment, chart, students);

    // Try swap
    const swappedAssignments = new Map(assignments);
    swappedAssignments.set(seatIdA, studentB);
    swappedAssignments.set(seatIdB, studentA);

    const swappedAssignment: SeatingAssignment = {
        seatAssignments: swappedAssignments,
        timestamp: Date.now()
    };
    const swappedScore = scoreAssignment(swappedAssignment, chart, students);

    return {
        improved: swappedScore > currentScore,
        scoreDelta: swappedScore - currentScore
    };
}

/**
 * Hill climbing optimization: iteratively swap students to improve constraint satisfaction
 */
export function hillClimbingOptimization(
    assignments: Map<string, string | undefined>,
    chart: SeatingChart,
    students: Student[],
    maxIterations: number = 100
): Map<string, string | undefined> {
    const result = new Map(assignments);
    const seatIds = Array.from(result.keys());

    let improved = true;
    let iterations = 0;

    while (improved && iterations < maxIterations) {
        improved = false;
        iterations++;

        // Try all pairs of seats
        const shuffledSeats = shuffle([...seatIds]);

        for (let i = 0; i < shuffledSeats.length && !improved; i++) {
            for (let j = i + 1; j < shuffledSeats.length && !improved; j++) {
                const seatA = shuffledSeats[i];
                const seatB = shuffledSeats[j];

                if (!seatA || !seatB) continue;

                const { improved: swapImproved } = trySwap(
                    seatA,
                    seatB,
                    result,
                    chart,
                    students
                );

                if (swapImproved) {
                    // Apply the swap
                    const studentA = result.get(seatA);
                    const studentB = result.get(seatB);
                    result.set(seatA, studentB);
                    result.set(seatB, studentA);
                    improved = true;
                }
            }
        }
    }

    return result;
}

/**
 * Simulated annealing optimization for escaping local minima
 */
export function simulatedAnnealingOptimization(
    assignments: Map<string, string | undefined>,
    chart: SeatingChart,
    students: Student[],
    options: {
        initialTemp?: number;
        coolingRate?: number;
        maxIterations?: number;
    } = {}
): Map<string, string | undefined> {
    const {
        initialTemp = 100,
        coolingRate = 0.95,
        maxIterations = 500
    } = options;

    const result = new Map(assignments);
    const seatIds = Array.from(result.keys()).filter(id => result.get(id) !== undefined);

    if (seatIds.length < 2) return result;

    let temp = initialTemp;
    let currentAssignment: SeatingAssignment = {
        seatAssignments: result,
        timestamp: Date.now()
    };
    let currentScore = scoreAssignment(currentAssignment, chart, students);

    for (let i = 0; i < maxIterations && temp > 1; i++) {
        // Pick two random occupied seats to swap
        const shuffled = shuffle([...seatIds]);
        const seatA = shuffled[0];
        const seatB = shuffled[1];

        if (!seatA || !seatB || seatA === seatB) continue;

        // Try swap
        const studentA = result.get(seatA);
        const studentB = result.get(seatB);

        result.set(seatA, studentB);
        result.set(seatB, studentA);

        const newAssignment: SeatingAssignment = {
            seatAssignments: result,
            timestamp: Date.now()
        };
        const newScore = scoreAssignment(newAssignment, chart, students);

        // Accept if better, or probabilistically if worse
        if (newScore > currentScore) {
            currentScore = newScore;
        } else {
            const delta = currentScore - newScore;
            const probability = Math.exp(-delta / temp);

            if (Math.random() < probability) {
                currentScore = newScore;
            } else {
                // Revert swap
                result.set(seatA, studentA);
                result.set(seatB, studentB);
            }
        }

        temp *= coolingRate;
    }

    return result;
}
